import { useState, type ReactNode } from 'react';
import {
  formatDurationNumber,
  parseDuration,
  parseHours,
  type DurationUnit,
} from '@forecastify/shared';
import type {
  AgentProposalField,
  AgentProposalState,
  StoredAgentProposal,
} from '@forecastify/shared';
import { Button, Icon, TextInput, Textarea, Tooltip } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { DurationUnitPicker } from '../../duration-input';
import { useI18n, useT, type MessageKey, type Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C27.3 · the confirmation. R-25, and the mockup's own promise kept.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's line under the composer read *"Írási művelet előtt megerősítést kér"* — it asks for
 * confirmation before a write. C17 deleted that line for describing an agent that did not exist.
 * This is the thing it described.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THIS CARD IS UX. THE GATE IS THE SERVER'S.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Rule 6: *"UI gating is UX; the server check is the only security."* Nothing here decides whether a
 * write may happen — the proposal is a row, it expires in fifteen minutes, it is single-use, and the
 * accept endpoint executes the input **it** stored. A card that failed to render would leave a write
 * unperformed rather than performed.
 *
 * ⚠️⚠️ **THE SENTENCE COMES FROM THE SERVER, AND IT IS THE ONE PLACE IN THIS PRODUCT WHERE THAT IS
 * TRUE.** Every other frame carries a machine identifier that this app translates. Here the summary
 * is composed by the API in the reader's own locale, because the words somebody approves and the row
 * that gets written must come from the same call — a client re-deriving *"8:00 óra — HSO-131"* from a
 * payload would be a second implementation of the one string that must not be able to disagree with
 * what happens. `docs/plan/agent.md` §5, and `agent/proposal-copy.ts` holds both languages.
 *
 * ⚠️⚠️ **AND IT DOES NOT SAY *"RÖGZÍTVE"* AFTER THE BUTTON IS PRESSED.** The accept request can
 * succeed while the write is refused — a closed month, a full day, somebody else's change — and the
 * only thing that knows which is the reply that follows. So the card says *approved*, and what
 * happened is the model's next sentence, immediately below it. A card claiming the row exists would
 * be the first fabricated confirmation in the product.
 */

/**
 * One proposal, as the thread holds it.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE CONTRACT'S OWN SHAPE, NOT A SECOND COPY OF IT. C27.4.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C27.3 declared this interface here, because a proposal only ever arrived on a live frame and the
 * card's state was the client's business. C27.4 sends four of the six states over the wire — a
 * reopened conversation has to render a card whose fate was decided in another tab, or last week — so
 * the union moved into `packages/shared` and this is an alias. Two unions would drift the first time a
 * state was added, and the drift would be silent: an unknown state renders as *pending*, with two
 * live buttons on a write somebody already approved.
 */
export type ThreadProposal = StoredAgentProposal;

/** ⚠️ The contract's enum, re-exported so `agent-route.tsx` keeps importing it from the card. */
export type ProposalState = AgentProposalState;

export interface AgentProposalCardProps {
  readonly proposal: ThreadProposal;
  readonly onAccept: (id: string) => void;
  readonly onDecline: (id: string) => void;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C46 · A CORRECTION IS NOT AN ACCEPT WITH A PAYLOAD. IT ASKS THE  │
   * │ SERVER FOR A NEW CARD.                                               │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * R-25: the accept endpoint executes the input **it** stored. So editing a field posts the values
   * to `revise`, which merges them into that stored input, re-runs the write tool's own `run` — same
   * schema, same lookups, same sentence — and answers with a proposal somebody still has to press.
   * This callback hands the values over and the route swaps the card for what comes back.
   */
  readonly onRevise: (id: string, values: Readonly<Record<string, string | null>>) => void;
}

/**
 * What a write is called.
 *
 * ⚠️ **One `t()` per branch with a LITERAL key**, because `i18n:check` reads literal arguments only —
 * `t(\`agent.proposal.\${tool}\`)` would make every one of these report as an orphan, and the fix
 * somebody reaches for is deleting them.
 *
 * ⚠️ **An unknown tool still gets a heading**, for the same reason a tool row does: a version skew
 * between the API and the bundle must not render a card with no title over two live buttons.
 */
function heading(t: Translate, tool: string, many: boolean): string {
  if (tool === 'worklogs_log') {
    return many ? t('agent.proposal.worklogs_log_many') : t('agent.proposal.worklogs_log');
  }
  if (tool === 'tasks_update') {
    return many ? t('agent.proposal.tasks_update_many') : t('agent.proposal.tasks_update');
  }
  if (tool === 'tasks_create') {
    return many ? t('agent.proposal.tasks_create_many') : t('agent.proposal.tasks_create');
  }
  if (tool === 'worklogs_update') {
    return many ? t('agent.proposal.worklogs_update_many') : t('agent.proposal.worklogs_update');
  }
  /* ⚠️ C49 · R-36's two. The heading says *törlés* rather than *megerősítés*, because the verb is the
     thing somebody has to notice before they read anything else on the card. */
  if (tool === 'worklogs_delete') {
    return many ? t('agent.proposal.worklogs_delete_many') : t('agent.proposal.worklogs_delete');
  }
  if (tool === 'tasks_delete') {
    return many ? t('agent.proposal.tasks_delete_many') : t('agent.proposal.tasks_delete');
  }
  /* ⚠️ C44 · the contract spine's four writes. */
  if (tool === 'contracts_create') {
    return many ? t('agent.proposal.contracts_create_many') : t('agent.proposal.contracts_create');
  }
  if (tool === 'contracts_update') {
    return many ? t('agent.proposal.contracts_update_many') : t('agent.proposal.contracts_update');
  }
  if (tool === 'line_items_create') {
    return many
      ? t('agent.proposal.line_items_create_many')
      : t('agent.proposal.line_items_create');
  }
  if (tool === 'line_items_update') {
    return many
      ? t('agent.proposal.line_items_update_many')
      : t('agent.proposal.line_items_update');
  }
  return t('agent.proposal.unknown');
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C49 · WHETHER THIS CARD IS THE IRREVERSIBLE KIND. R-36.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The icon comment below used to read *"never a filled destructive colour: this is a change somebody
 * asked for, not a warning. The design system reserves `destructive` for what cannot be undone."*
 * That reservation is exactly right and it now has an occupant: a deletion is the one proposal this
 * product cannot take back, so the card wears the colour the design system was holding for it.
 *
 * ⚠️ **Derived from the tool NAME rather than a flag on the wire**, which is the same call `heading`
 * makes one function above and for the same reasons: the name is already the card's identity, and a
 * `destructive` boolean would be a rendering hint travelling as data — with a migration behind it,
 * since a proposal is a stored row a reopened conversation re-reads.
 *
 * ⚠️⚠️ **AND AN UNKNOWN TOOL IS NOT DESTRUCTIVE, DELIBERATELY.** A version skew already renders an
 * unrecognised card under a generic heading; painting it red as well would be this component
 * guessing at the severity of something it cannot name. The server's own sentence — which is always
 * rendered — is what says what will happen.
 */
function destroys(tool: string): boolean {
  return tool === 'worklogs_delete' || tool === 'tasks_delete';
}

/** What a settled card says instead of its buttons. */
const SETTLED: Readonly<Record<string, MessageKey>> = {
  approved: 'agent.proposal.approved',
  declined: 'agent.proposal.declined',
  expired: 'agent.proposal.expired',
  used: 'agent.proposal.used',
};

export function AgentProposalCard({
  proposal,
  onAccept,
  onDecline,
  onRevise,
}: AgentProposalCardProps): ReactNode {
  const t = useT();
  /**
   * ⚠️ 3 Nov 2026 · an `hours` field arrives as the server writes hours (`8:00`) and is SHOWN and
   * TYPED in the reader's unit; what goes back to `revise` is hours again (`decisions.md` DU-1).
   */
  const { unit } = useDuration();
  const { locale } = useI18n();
  /**
   * DU-2 · each hours field's own picker, keyed on the proposal like the edits below. An untouched
   * field is redrawn in the unit its picker moves to; typing is relabelled (`duration-input.tsx`).
   */
  const [units, setUnits] = useState<{
    readonly id: string;
    readonly byKey: Readonly<Record<string, DurationUnit>>;
  }>({ id: proposal.id, byKey: {} });
  const unitOf = (key: string): DurationUnit =>
    (units.id === proposal.id ? units.byKey[key] : undefined) ?? unit;
  const shown = (field: AgentProposalField): string => {
    if (field.kind !== 'hours') return field.value;
    const hours = parseHours(field.value);
    return hours === null ? field.value : formatDurationNumber(hours, unitOf(field.key), locale);
  };
  const settled = SETTLED[proposal.state];
  /* ⚠️ C49 · see `destroys`: what the card looks like and what its button says both turn on this. */
  const destructive = destroys(proposal.tool);
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C46 · THE EDITS SO FAR, KEYED ON THE PROPOSAL'S ID.              │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ **The id is in the state, not in a `key` on the element**, because a revision replaces the
   * proposal in place: same turn, same position in the thread, new id. Without this the corrected
   * card would open with the previous card's half-typed values in it — React keeps the component
   * mounted and its state with it.
   */
  const [edited, setEdited] = useState<{
    readonly id: string;
    readonly values: Readonly<Record<string, string>>;
  }>({ id: proposal.id, values: {} });
  const values = edited.id === proposal.id ? edited.values : {};

  /* ⚠️ One row, because a form exists only for a single write — `agent_proposals.rows`, and 0094
   * argues why a batch keeps its read-only list. */
  const form = proposal.rows[0];
  const editable = form?.fields.some((entry) => entry.kind !== null) ?? false;
  const dirty = Object.keys(values).length > 0;

  return (
    <section
      className="bg-card mx-auto w-full max-w-[680px] rounded-xl border p-4 shadow-sm"
      /* ⚠️ A hook for the tests and for anybody reading the DOM: the state is the interesting fact
         about this card, and it is the thing a screenshot cannot show. */
      data-agent-proposal={proposal.state}
      aria-labelledby={`proposal-${proposal.id}`}
    >
      <div className="flex items-center gap-2">
        {/* ⚠️ **The pencil is never a filled destructive colour**: an ordinary write is a change
            somebody asked for, not a warning, and the design system reserves `destructive` for what
            cannot be undone. ⚠️⚠️ **C49 is what it was reserving it for** — a deletion gets the trash
            icon in the destructive foreground, which is the tint rather than the fill, because the
            fill belongs to the button somebody has to press. */}
        <Icon
          name={destructive ? 'trash' : 'pencil'}
          size={13}
          className={destructive ? 'text-destructive' : 'text-muted-foreground'}
          aria-hidden
        />
        <h3
          id={`proposal-${proposal.id}`}
          className="text-[11px] font-medium tracking-wide uppercase"
        >
          {/* ⚠️ **The heading agrees with the list under it (C27.6)**, and it carries no count: the
              number belongs in the server's headline, beside the figures it was counted from. */}
          {heading(t, proposal.tool, proposal.details.length > 0)}
        </h3>
      </div>

      {/* ⚠️ **The server's sentence, rendered as it arrived.** Nothing here reformats a figure or a
          date: the whole point is that this text and the row that gets written are one decision. */}
      <p className="mt-2 text-[13.5px] leading-relaxed">{proposal.summary}</p>

      {/*
        ┌─────────────────────────────────────────────────────────────────────┐
        │ ⚠️⚠️ ONE ROW PER WRITE, AND A CARD WITH SEVEN OF THEM IS STILL ONE   │
        │ BUTTON. C27.6.                                                      │
        └─────────────────────────────────────────────────────────────────────┘

        A write tool takes a list, so `summary` above is a headline — *"5 bejegyzés rögzítése ·
        összesen 10:00 óra"* — and these are the rows it covers. ⚠️⚠️ **THEY ARE NOT OPTIONAL
        DETAIL:** a card that showed the count alone would ask somebody to approve a number, and
        every argument for the confirmation gate is that they approve the *rows*.

        ⚠️ **Empty for a single write**, whose card is unchanged from C27.3 — the server sends no
        lines rather than a list of one, so there is nothing to hide here.

        ⚠️ **Scrolls rather than truncating.** Fifty entries is the schema's own limit and it is a
        long card; hiding rows behind a *"+ 12 more"* would put the part somebody has not read
        inside the thing they are consenting to. `AGENT_BATCH_LIMIT` bounds how long this can get.
      */}
      {proposal.details.length === 0 ? null : (
        <ul className="border-border/60 mt-2.5 max-h-[15rem] overflow-y-auto border-l pl-3">
          {proposal.details.map((line, index) => (
            <li
              /* ⚠️ The index, deliberately: these are lines of text with no identity of their own,
                 and two identical lines in one batch is a real thing (two hours logged twice on one
                 day). A key derived from the text would collide exactly there. */
              key={index}
              className="text-muted-foreground py-0.5 text-[12.5px] leading-snug tabular-nums"
            >
              {line}
            </li>
          ))}
        </ul>
      )}

      {/*
        ┌─────────────────────────────────────────────────────────────────────┐
        │ ⚠️⚠️ THE FIELDS, AND THE PLANS' §0.2 IS WHY THEY ARE INPUTS RATHER  │
        │ THAN A LIST. C46.                                                   │
        └─────────────────────────────────────────────────────────────────────┘

        *"A widget mindig teljes. Nem »mentsem?«, hanem minden mező kitöltve, szerkeszthetően. A
        felhasználó a widgetben javít, nem chatben."* The values come from the server, composed by
        the same call as the sentence above them; what a person types is sent to `revise`, which
        answers with a new card. Nothing here writes.

        ⚠️ **A field with no `kind` renders as TEXT**, and that is the server's decision rather than
        this component's: an id-valued field — a status, an assignee, a line item — shows the name it
        resolved to, and `revise` refuses a value for it. One column, two jobs.
      */}
      {form === undefined ? null : (
        <dl className="mt-3 flex flex-col gap-2">
          {form.fields.map((entry) => (
            <ProposalFieldRow
              key={entry.key}
              field={entry}
              value={values[entry.key] ?? shown(entry)}
              disabled={settled !== undefined || proposal.state === 'working'}
              unit={unitOf(entry.key)}
              onUnit={(next) => {
                setUnits((held) => ({
                  id: proposal.id,
                  byKey: { ...(held.id === proposal.id ? held.byKey : {}), [entry.key]: next },
                }));
              }}
              onChange={(next) => {
                setEdited((held) => ({
                  id: proposal.id,
                  values: { ...(held.id === proposal.id ? held.values : {}), [entry.key]: next },
                }));
              }}
            />
          ))}
        </dl>
      )}

      {settled === undefined ? (
        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            /**
             * ⚠️⚠️ **C49 · THE BUTTON SAYS THE VERB AND WEARS THE COLOUR. R-36.**
             *
             * *Rögzítés* on a card that deletes eight hours would be the single most misleading
             * control in the product: the label a person reads at the moment of pressing has to be
             * the thing that then happens. And `destructive` is the design system's own signal for
             * *this cannot be undone*, which is precisely the fact that separates these two writes
             * from the other eight.
             */
            variant={destructive ? 'destructive' : 'default'}
            onClick={() => {
              onAccept(proposal.id);
            }}
            /* ⚠️ **Refused while there are unsent edits.** The accept executes the STORED input, so
               pressing it with a changed box on screen would write the old value — the one failure
               this whole card exists to prevent, arriving through the feature that lets somebody fix
               it. `agent.proposal.dirty` is the sentence that says so. */
            disabled={dirty || proposal.state === 'working'}
            aria-disabled={dirty || proposal.state === 'working'}
          >
            {destructive ? t('agent.proposal.accept_delete') : t('agent.proposal.accept')}
          </Button>
          {/*
            ⚠️⚠️ **THE CORRECTION IS ITS OWN BUTTON AND IT IS NOT THE PRIMARY ONE.** Two reasons, and
            the second is the one that matters: a person who edits a field and presses *Rögzítés*
            would be accepting the figure the server stored rather than the one on their screen, so
            the accept button is DISABLED while there are unsent edits — the card says which button
            to press instead of quietly ignoring what they typed.

            ⚠️ Absent when nothing is editable, because a control that governs nothing is worse than
            one that is missing (`agent-screen.tsx` makes the same call about the composer's).
          */}
          {editable ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                onRevise(
                  proposal.id,
                  Object.fromEntries(
                    Object.entries(values).map(([key, text]) => {
                      const kind = form?.fields.find((entry) => entry.key === key)?.kind;
                      /* DU-2 · hours typing goes as it stands — the server reads it as hours; days
                         typing, or a day suffix (`1,5n`), is turned into hours here. Unreadable
                         typing goes as typed, so the server's refusal names it. */
                      const hours =
                        kind === 'hours' && (unitOf(key) === 'days' || parseHours(text) === null)
                          ? parseDuration(text, unitOf(key))
                          : null;
                      return [key, hours === null ? text : String(hours)];
                    }),
                  ),
                );
              }}
              disabled={!dirty || proposal.state === 'working'}
              aria-disabled={!dirty || proposal.state === 'working'}
            >
              {t('agent.proposal.revise')}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              onDecline(proposal.id);
            }}
            disabled={proposal.state === 'working'}
            aria-disabled={proposal.state === 'working'}
          >
            {t('agent.proposal.decline')}
          </Button>
          {/* ⚠️ **The expiry is stated rather than counted down.** A ticking clock re-renders the
              thread every second for a number nobody watches, and the refusal is the real mechanism:
              a card pressed too late says so. */}
          <span className="text-muted-foreground ml-auto text-[11px]">
            {proposal.state === 'working'
              ? t('agent.proposal.working')
              : dirty
                ? /* ⚠️ Why the primary button went quiet. A disabled control with no sentence beside
                     it reads as a broken page — `state-rules.md`'s rule about refusals. */
                  t('agent.proposal.dirty')
                : t('agent.proposal.window')}
          </span>
        </div>
      ) : (
        <p
          className="text-muted-foreground mt-3 flex items-center gap-1.5 text-[11.5px]"
          /* ⚠️ `status`, not `alert`: an expired or already-answered card is information, and an
             assertive announcement on every render interrupts a screen reader mid-sentence. */
          role="status"
        >
          <Icon
            name={proposal.state === 'approved' ? 'check' : 'x'}
            size={12}
            aria-hidden
            className={proposal.state === 'approved' ? undefined : 'text-muted-foreground'}
          />
          {t(settled)}
        </p>
      )}
    </section>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C46 · one field of a confirmation card. §0.2 and §0.4                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`kind === null` MEANS READ-ONLY AND IT IS THE SERVER'S WORD, NOT A GUESS FROM THE VALUE.**
 * An id-valued field — a status, an assignee, a project, a line item — shows the NAME it resolved to
 * and takes a uuid, so an input box would be asking somebody to type an id. `revise` refuses a value
 * for exactly these fields, so what renders here and what the server accepts come from one column.
 *
 * ⚠️⚠️ **AND `derived` IS §0.4's WHOLE REQUIREMENT: `~` PREFIX, A MICRO-LABEL, AND THE REASON ON
 * HOVER.** A suggestion that is not visibly a suggestion is the fabrication the preamble forbids;
 * this is what makes it visible. The tilde goes beside the field rather than inside the value,
 * because the value is in an input box and a person who edited it would send the tilde back.
 */
function ProposalFieldRow({
  field,
  value,
  disabled,
  onChange,
  unit,
  onUnit,
}: {
  readonly field: AgentProposalField;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (next: string) => void;
  /** DU-2 · an `hours` field's unit and its picker. */
  readonly unit: DurationUnit;
  readonly onUnit: (next: DurationUnit) => void;
}): ReactNode {
  const t = useT();
  const id = `field-${field.key}`;

  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
      <dt className="text-muted-foreground w-32 shrink-0 text-[11.5px]">
        <label htmlFor={field.kind === null ? undefined : id}>{field.label}</label>
        {field.derived === null ? null : (
          /* ⚠️ **A kit `Tooltip`, so the reason is on hover, and the text is ALSO the accessible name** —
             §0.4 asks for the justification on hover and a tooltip nobody can reach by keyboard
             would be the same information hidden from half the people who need it. */
          <Tooltip asChild content={field.derived}>
            <span
              className="text-muted-foreground/80 ml-1 cursor-help"
              aria-label={`${t('agent.proposal.derived')}: ${field.derived}`}
            >
              ~
            </span>
          </Tooltip>
        )}
      </dt>
      <dd className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px]">
        {field.kind === null ? (
          /* ⚠️ Not an input, not a disabled input: a greyed-out box invites a click that does
             nothing. This is a value somebody is reading. */
          <span className="tabular-nums">{value === '' ? '–' : value}</span>
        ) : field.kind === 'note' ? (
          <Textarea
            id={id}
            value={value}
            disabled={disabled}
            rows={2}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        ) : (
          <TextInput
            id={id}
            /* ⚠️⚠️ **A DATE FIELD IS `type="date"` AND AN HOUR FIELD IS NOT.** The date input gives a
               picker and enforces `yyyy-mm-dd`, which is exactly the wire form. Hours are not:
               `parseHours` accepts `8`, `8h`, `8:00` and `8,0` deliberately — that latitude is a
               product decision (C13) — and a `type="number"` box would refuse three of the four
               things people type. */
            type={field.kind === 'date' ? 'date' : 'text'}
            value={value}
            disabled={disabled}
            inputMode={field.kind === 'hours' ? 'decimal' : undefined}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        )}
        {field.kind === 'hours' ? (
          <DurationUnitPicker unit={unit} disabled={disabled} onChange={onUnit} />
        ) : null}
      </dd>
    </div>
  );
}
